import styled from "styled-components";
import { useEffect } from "react";
import { useTranslation, Trans } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { PRIVACY_SEO } from '../../seo/pageSeoMeta';
import { purpleColors, violetColors, slateColors, Section, Container } from "./AppLandingPage/styles";

const PageSection = styled(Section)`
  background: linear-gradient(to bottom right, ${slateColors[50]}, white);
  padding-top: 4rem;
  padding-bottom: 5rem;
`;

const PageContainer = styled(Container)`
  max-width: 70rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const HeroCard = styled.div`
  background: linear-gradient(to right, ${purpleColors[50]}, ${violetColors[500]}12);
  border: 1px solid ${purpleColors[100]};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);

  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${slateColors[200]};
  border-radius: 1.25rem;
  padding: 1.75rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (min-width: 768px) {
    padding: 2.25rem;
    gap: 1.5rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 2.25rem;
  font-weight: 800;
  color: ${slateColors[900]};
  line-height: 1.1;

  @media (min-width: 768px) {
    font-size: 2.75rem;
  }
`;

const MetaText = styled.p`
  margin: 0.5rem 0 0;
  color: ${slateColors[600]};
  font-size: 1rem;

  @media (min-width: 768px) {
    font-size: 1.05rem;
  }
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const SectionTitle = styled.h2`
  margin: 0.9rem 0 0.05rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${slateColors[900]};

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Paragraph = styled.p`
  margin: 0.1rem 0 0;
  color: ${slateColors[700]};
  font-size: 1rem;
  line-height: 1.65;

  @media (min-width: 768px) {
    font-size: 1.05rem;
  }

  a {
    color: ${purpleColors[600]};
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: ${purpleColors[700]};
    }
  }
`;

const List = styled.ul`
  padding-left: 1.25rem;
  margin: 0.25rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: ${slateColors[700]};
  font-size: 1rem;
  line-height: 1.6;

  @media (min-width: 768px) {
    font-size: 1.05rem;
  }
`;

const ListItem = styled.li`
  margin: 0;
`;

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();

  useEffect(() => {
      window.scrollTo(0, 0);
  }, []);

    return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{PRIVACY_SEO.title}</title>
        <meta name="description" content={PRIVACY_SEO.description} />
        <meta name="keywords" content="chuuchuu privacy policy, train app privacy, railway data privacy, data protection, GDPR compliance, local storage" />

        <meta name="prerender-status-code" content="200" />
        <meta name="prerender-header" content="Cache-Control: max-age=300, must-revalidate" />
        <meta name="fragment" content="!" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chuuchuu.com/privacy" />
        <meta property="og:title" content={PRIVACY_SEO.title} />
        <meta property="og:description" content={PRIVACY_SEO.description} />
        <meta property="og:image" content="https://chuuchuu.com/images/previewpage.jpg" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://chuuchuu.com/privacy" />
        <meta property="twitter:title" content={PRIVACY_SEO.title} />
        <meta property="twitter:description" content={PRIVACY_SEO.description} />
        <meta property="twitter:image" content="https://chuuchuu.com/images/previewpage.jpg" />

        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://chuuchuu.com/privacy" />
      </Helmet>

      <PageSection>
        <PageContainer>
          <HeroCard>
            <PageTitle>{t('privacy.title')}</PageTitle>
            <MetaText>{t('privacy.lastUpdated')}</MetaText>
          </HeroCard>

          <Card>
            <SectionBlock>
              <Paragraph>
                {t('privacy.intro')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section1Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section1Content')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section2Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section2Content')}
              </Paragraph>
              <List>
                <ListItem>{t('privacy.section2EventData')}</ListItem>
                <ListItem>{t('privacy.section2DeviceData')}</ListItem>
                <ListItem>{t('privacy.section2Identifier')}</ListItem>
                <ListItem>{t('privacy.section2IPNote')}</ListItem>
                <ListItem>{t('privacy.section2Residency')}</ListItem>
              </List>
              <Paragraph>
                <Trans
                  i18nKey="privacy.section2MixpanelLink"
                  components={{
                    link: (
                      <a
                        href="https://mixpanel.com/legal/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ),
                  }}
                />
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section3Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section3Intro')}
              </Paragraph>
              <Paragraph>
                {t('privacy.section3Web')}
              </Paragraph>
              <Paragraph>
                {t('privacy.section3iOS')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section4Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section4Content')}
              </Paragraph>
              <List>
                <ListItem>
                  <strong>{t('privacy.section4RememberingLocations')}</strong>
                </ListItem>
                <ListItem>
                  <strong>{t('privacy.section4OptionsSettings')}</strong>
                </ListItem>
              </List>
              <Paragraph>
                {t('privacy.section4LocalStorage')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section5Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section5Content')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section6Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section6Content')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section7Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section7Content')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section8Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section8Content')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section9Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section9Content')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section10Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section10Content')}
              </Paragraph>
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t('privacy.section11Title')}</SectionTitle>
              <Paragraph>
                {t('privacy.section11Content')}
              </Paragraph>
              <Paragraph>
                <a href={`mailto:${t('privacy.contactEmail')}`}>{t('privacy.contactEmail')}</a>
              </Paragraph>
            </SectionBlock>
          </Card>
        </PageContainer>
      </PageSection>
    </>
    );
  };

  export default PrivacyPolicyPage;
